import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useUpdateDocument, usePublishDocument } from "@/models/resourceHubs";
import { useRichEditorHandlers } from "@/hooks/useRichEditorHandlers";
import { useSubscriptionsAdapter } from "@/models/subscriptions";
import { useMe } from "@/contexts/CurrentCompanyContext";
import { compareIds, usePaths } from "@/routes/paths";
import { assertPresent } from "@/utils/assertions";
import { DocumentEditPage, showErrorToast, SubscribersSelector, type DocumentEditPageTypes } from "turboui";

import { useLoadedData } from "./loader";
import { buildEditDocumentPageNavigation } from "./navigation";

export function Page() {
  const { t } = useTranslation();
  const { document } = useLoadedData();
  const me = useMe();
  const paths = usePaths();
  const navigate = useNavigate();

  const mutationScope = {
    spaceId: document.space?.id,
    resourceHubId: document.resourceHubId,
    parentFolderId: document.parentFolderId,
  };

  const { mutateAsync: edit } = useUpdateDocument(mutationScope);
  const { mutateAsync: publish } = usePublishDocument(mutationScope);

  const isDraft = document.state === "draft";
  const canPublish = Boolean(document.author && me && compareIds(me.id, document.author.id));

  assertPresent(document.potentialSubscribers, "potentialSubscribers must be present in document");
  assertPresent(document.subscriptionList, "subscriptionList must be present in document");
  assertPresent(document.resourceHub, "resourceHub must be present in document");
  assertPresent(document.name, "name must be present in document");
  assertPresent(document.content, "content must be present in document");
  assertPresent(document.resourceHubId, "resourceHubId must be present in document");

  const subscriptionsState = useSubscriptionsAdapter(document.potentialSubscribers ?? [], {
    ignoreMe: true,
    sendNotificationsToEveryone: document.subscriptionList?.sendToEveryone ?? undefined,
    resourceHubName: document.resourceHub.name,
  });

  const initialSubscriptionsRef = React.useRef<{
    subscriptionType: SubscribersSelector.SubscriptionOption;
    subscriberIds: string[];
  } | null>(null);

  if (initialSubscriptionsRef.current === null) {
    initialSubscriptionsRef.current = {
      subscriptionType: subscriptionsState.subscriptionType,
      subscriberIds: [...subscriptionsState.currentSubscribersList],
    };
  }

  const richTextHandlers = useRichEditorHandlers({ scope: { type: "resource_hub", id: document.resourceHubId } });
  const cancelLink = paths.resourceHubDocumentPath(document.id!);
  const initialContent = JSON.parse(document.content);

  async function handleSubmit(
    values: DocumentEditPageTypes.Values,
    meta: { action: "save" | "publish-draft"; contentChanged: boolean },
  ) {
    try {
      const serializedContent = JSON.stringify(values.content);
      const subscriptionPayload = {
        sendNotificationsToEveryone: subscriptionsState.notifyEveryone,
        subscriberIds: subscriptionsState.currentSubscribersList,
      };
      const subscriptionsChanged = hasSubscriptionsChanged(
        isDraft,
        initialSubscriptionsRef.current,
        subscriptionsState,
      );

      if (meta.action === "save") {
        if (meta.contentChanged || subscriptionsChanged) {
          await edit({
            documentId: document.id,
            name: values.title,
            content: serializedContent,
            ...(isDraft ? subscriptionPayload : {}),
          });
        }
      } else {
        await publish({
          documentId: document.id,
          name: values.title,
          content: serializedContent,
          ...(isDraft ? subscriptionPayload : {}),
        });
      }

      navigate(cancelLink);
      return true;
    } catch {
      showErrorToast(t("Document not updated"), t("Check the form and try again."));
      return false;
    }
  }

  const shared = {
    pageTitle: t("Edit Document"),
    navigation: buildEditDocumentPageNavigation(document, paths),
    testId: "resource-hub-edit-document-page",
    richTextHandlers,
    initialTitle: document.name,
    initialContent,
    cancelLink,
    onSubmit: handleSubmit,
  };

  if (!isDraft) {
    return <DocumentEditPage {...shared} hideSubscriptions hidePublishAction />;
  }

  const subscriptions = {
    subscribers: subscriptionsState.subscribers,
    selectedSubscribers: subscriptionsState.selectedSubscribers,
    onSelectedSubscribersChange: subscriptionsState.onSelectedSubscribersChange,
    subscriptionType: subscriptionsState.subscriptionType,
    onSubscriptionTypeChange: subscriptionsState.onSubscriptionTypeChange,
    alwaysNotify: subscriptionsState.alwaysNotify,
    allSubscribersLabel: subscriptionsState.allSubscribersLabel,
  };

  if (!canPublish) {
    return <DocumentEditPage {...shared} subscriptions={subscriptions} hidePublishAction />;
  }

  return <DocumentEditPage {...shared} subscriptions={subscriptions} />;
}

function hasSubscriptionsChanged(
  isDraft: boolean,
  initialSubscriptions: { subscriptionType: SubscribersSelector.SubscriptionOption; subscriberIds: string[] } | null,
  currentState: { subscriptionType: SubscribersSelector.SubscriptionOption; currentSubscribersList: string[] },
) {
  if (!isDraft) return false;
  if (!initialSubscriptions) return false;

  const typeChanged = initialSubscriptions.subscriptionType !== currentState.subscriptionType;
  const subscribersChanged = !areIdListsEqual(initialSubscriptions.subscriberIds, currentState.currentSubscribersList);

  return typeChanged || subscribersChanged;
}

function areIdListsEqual(initialIds: string[] | null, currentIds: string[]) {
  if (!initialIds) return currentIds.length === 0;
  if (initialIds.length !== currentIds.length) return false;

  const sortedInitial = [...initialIds].sort();
  const sortedCurrent = [...currentIds].sort();

  return sortedInitial.every((id, index) => id === sortedCurrent[index]);
}
